import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  CircularProgress,
  Chip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ItemsFilterDialog from "../../components/Inventory/ItemsFilterDialog";
import { searchStyles } from "../../styles/formStyles";
import { getInventoryWithFilters } from "../../services/Inventory";

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  In_Stock: { bg: "rgba(0, 178, 33, 0.1)", color: "#00B221" },
  Low_Stock: { bg: "rgba(243, 150, 0, 0.1)", color: "#F39600" },
  Out_Of_Stock: { bg: "rgba(254, 9, 9, 0.1)", color:"#FE0909" },
};

const Items =() => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(7); // Default page size
  const [loading, setLoading] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<{ statuses: string[]; itemTypeIds: number[] }>({ statuses: [], itemTypeIds: [] });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (searchParams.get("action") === "add") {
      navigate("/inventory/items/new", { replace: true });
    }
  }, [searchParams, navigate]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getInventoryWithFilters({
        searchTerm: debouncedSearch,
        statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
        itemTypeIds: filters.itemTypeIds.length > 0 ? filters.itemTypeIds : undefined,
        page: page - 1,
        size: pageSize
      });
      
      // Handle paginated response structure
      if (response && typeof response === 'object') {
        const items = response.content || response.data || response || [];
        setRows(Array.isArray(items) ? items : []);
        setTotalPages(response.totalPages || Math.ceil((response.totalElements || 0) / pageSize));
        setTotalElements(response.totalElements || 0);
        // Update page size if it comes from API response
        if (response.size && response.size !== pageSize) {
          setPageSize(response.size);
        }
      } else {
        setRows([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (e) {
      console.error("Failed to load items:", e);
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.statuses, filters.itemTypeIds, page, pageSize]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const paginatedRows = rows;

  return (
    <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", p: 1, gap: 2 }}>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ p:1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: "1px solid", borderColor: "grey.100" }}>
          <TextField
            placeholder="Search by SKU or Name"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            }}
            sx={searchStyles}
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              startIcon={<FilterAltOutlinedIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{ borderRadius: "6px", border: "1px solid rgba(74, 85, 101, 0.10)", backgroundColor: "#FFF", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", textTransform: "none", color: "text.primary", px: 2, "&:hover": { backgroundColor: "#FFF" } }}
            >
              Filter{" "}
              {(filters.statuses.length + filters.itemTypeIds.length) > 0 && (
                <Typography component="span" sx={{ ml: 0.5, color: "primary.main" }}>
                  ({filters.statuses.length + filters.itemTypeIds.length})
                </Typography>
              )}
            </Button>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={() => navigate("/inventory/items/new")}
              sx={{ borderRadius: "6px", textTransform: "none", px: 2, "&:hover": { backgroundColor: "primary.main" } }}
            >
              Add Item
            </Button>
          </Box>
        </Box>

        <Box sx={{ p:1, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Paper sx={{ maxHeight: "500px", overflow: "auto", "&::-webkit-scrollbar": { width: "8px", height: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <Table stickyHeader sx={{ minWidth: "600px" }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, py: 0.5, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>SKU</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Barcode Number</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Type</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Category</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Location</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "background.default", fontSize: "0.875rem", whiteSpace: "nowrap" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row: any) => (
                    <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.id}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.barcode || "..."}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.itemName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.itemType}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.category}</TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.875rem", p: 1 }}>{row.location || "..."}</TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Chip
                          label={row.status.replace(/_/g, ' ')}
                          sx={{
                            backgroundColor: STATUS_COLORS[row.status]?.bg || "grey.light",
                            color: STATUS_COLORS[row.status]?.color || "grey.primary",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            height: "1.75rem",
                            minWidth: "100px",
                            borderRadius: "6px",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                          <Button 
                            startIcon={<VisibilityOutlinedIcon />} 
                            onClick={() => {
                              console.log("View button clicked for item:", row);
                              navigate(`/inventory/items/view?id=${row.id}`, { state: { item: row } });
                            }}
                            sx={{ color: "grey.700", textTransform: "none", fontSize: "0.8rem", "&:hover": { backgroundColor: "transparent" } }}
                          >
                            View
                          </Button>
                          <Button
                            startIcon={<Box component="img" src="/edit.svg" alt="Edit" sx={{ width: "1.2rem", height: "1.2rem" }} />}
                            onClick={() => navigate("/inventory/items/edit", { state: { item: row } })}
                            sx={{ color: "primary.main", textTransform: "none", fontSize: "0.8rem", "&:hover": { backgroundColor: "transparent" } }}
                          >
                            Edit
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {totalElements > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" />
        </Box>
      )}

      <ItemsFilterDialog
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        onApply={(newFilters: any) => {
          setFilters(newFilters);
          setFilterAnchor(null);
        }}
      />
    </Box>
  );
};
export default Items;
